"use client";

import { useEffect } from "react";
import { useRouter } from "next/navigation";
import { createClient } from "@/lib/supabase/browser";

/**
 * Client-side component to handle hash-based auth tokens
 * This handles cases where Supabase redirects with tokens in the URL hash
 * (e.g., password reset links that redirect to homepage with #access_token=...)
 */
export function AuthHashHandler() {
  const router = useRouter();

  useEffect(() => {
    // Only run on client side
    if (typeof window === "undefined") return;

    // Check if there are auth tokens or errors in the hash
    const hash = window.location.hash;
    if (!hash) return;

    // Parse hash parameters
    const hashParams = new URLSearchParams(hash.replace(/^#/, ""));
    
    // Check for errors first (e.g., expired tokens)
    const error = hashParams.get("error");
    const errorCode = hashParams.get("error_code");
    const errorDescription = hashParams.get("error_description");
    
    if (error || errorCode) {
      // Handle error cases
      const isExpired = 
        errorCode === "otp_expired" || 
        errorCode === "token_expired" ||
        errorDescription?.toLowerCase().includes("expired") ||
        errorDescription?.toLowerCase().includes("invalid");
      
      const isAccessDenied = 
        error === "access_denied" ||
        errorCode === "access_denied";
      
      if (isExpired || isAccessDenied) {
        // Check if this was a password reset attempt
        const type = hashParams.get("type");
        if (type === "recovery") {
          // Redirect to forgot password page with expired token message
          router.replace("/forgot-password?error=expired&message=" + encodeURIComponent("This password reset link has expired. Please request a new one."));
          return;
        }
        
        // For other expired tokens, redirect to appropriate page
        router.replace("/auth/check-email?error=expired&message=" + encodeURIComponent("This link has expired. Please request a new one."));
        return;
      }
      
      // Other errors - redirect to error page
      router.replace("/auth/check-email?error=" + encodeURIComponent(error || "unknown"));
      return;
    }

    // Check for auth tokens
    const accessToken = hashParams.get("access_token");
    const refreshToken = hashParams.get("refresh_token");
    const type = hashParams.get("type");

    if (accessToken && refreshToken) {
      // Handle the auth tokens
      (async () => {
        try {
          const supabase = createClient();
          
          // Set the session
          const { error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });

          if (sessionError) {
            console.error("Error setting session from hash:", sessionError);
            
            // Check if it's an expired token error
            if (sessionError.message?.toLowerCase().includes("expired") || 
                sessionError.message?.toLowerCase().includes("invalid")) {
              if (type === "recovery") {
                router.replace("/forgot-password?error=expired&message=" + encodeURIComponent("This password reset link has expired. Please request a new one."));
                return;
              }
            }
            
            // Redirect to error page
            router.replace("/auth/check-email?error=invalid_code");
            return;
          }

          // Check if this is a password recovery flow
          if (type === "recovery") {
            // Redirect to password reset page
            router.replace("/auth/reset-password");
            return;
          }

          // For other types (signup, magiclink, etc.), redirect to complete page
          // which will handle the rest of the flow
          router.replace("/auth/complete");
        } catch (error) {
          console.error("Error handling auth hash:", error);
          router.replace("/auth/check-email?error=invalid_code");
        }
      })();
    }
  }, [router]);

  return null; // This component doesn't render anything
}

